import * as React from 'react';
import { FastField } from 'formik';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Checkbox, FormGroup, styled } from '@lib/components';
import { Button } from '@lib/components';
import { RotateLoader } from '@lib/components';
import { Input } from '@lib/components';
import { Switch } from '@lib/components';
import { Modal, ModalContent, ModalTitle } from '@lib/components';
import { UI } from '../../../../../../../core/ui';
import { MobxComponent } from '../../../../../../../mobx/components/index';
import { inject, observer } from 'mobx-react';
import { RestaurantForm } from '../../../../../../../mobx/components/restaurant-form';
import { config } from '../../../../../../../config';

interface Props {
  close?: () => void;
}

type FormValues =
  T.Schema.Restaurant.RestaurantSchema['settings']['services']['delivery']['providers'];

const ErrorBox = styled.div`
  color: red;
  padding: 4px 0;
`;

const InfoBox = styled.div`
    color: #084298;
    background-color: #cfe2ff;
    border-color: #b6d4fe;
    position: relative;
    padding: .75rem 1.25rem;
    border: 1px solid transparent;
    border-radius: .25rem;
    margin-top: 1rem;
`

const TermsUL = styled.ul`
  margin-top: 15px;
  margin-bottom: 15px;
  list-style: none;
  padding-left: 0px;
  & li {
    margin-bottom: 10px;
  }
`
interface State {
  termsModal: boolean;
}

@inject('store')
@observer
export class SettingsFormIntegrationUber extends MobxComponent<
  Props,
  State
> {
  initialValues: FormValues;
  webhookUrl: string;

  constructor(props: Props) {
    super(props);
    this.state = {
      termsModal: false,
    };
    const r = this.injected.store.restaurant!;
    this.initialValues = r.settings.services.delivery.providers;
    this.webhookUrl = `${config.urls.api2}/v1/uber/webhook`;
  }

  validateRequiredField(value: string, enabled: boolean) {
    let error;
    if (_isEmpty(value) && enabled) {
      error = 'This field value cannot be empty.';
    }
    return error;
  }

  validateRequiredCheckbox(value: any, enabled: boolean) {
    let error;
    if (value == false && enabled) {
      error = 'This field is required.';
    }
    return error;
  }

  getError(errors: any, key: string): string {
    if (errors && errors.uber) {
      const message = _get(errors.uber, key, '');
      return _isEmpty(message) ? '' : message;
    }
    return '';
  }

  render() {
    return (
      <>
      <RestaurantForm<FormValues>
        submit={async (r, values) => {
          r.settings.services.delivery.providers = values;
          const update = {
            $set: {
              'settings.services.delivery.providers':
                r.settings.services.delivery.providers
            },
          };
          return { r, update };
        }}
        initialValues={this.initialValues}
        onSuccess={() => {
          if (this.props.close) {
            this.props.close();
          }
        }}
        onError={() => UI.notification.error('An error occurred')}
        onSuccessMessage="Settings Updated"
      >
        {({ form, error }) => {
          const { isSubmitting, setFieldValue, errors } = form;
          return (
            <>
              <FastField
                name="uber.enabled"
                render={({ field }: any) => (
                  <FormGroup
                    title="Enabled"
                    help="Enabling this will automatically trigger the Uber delivery process for new orders."
                  >
                    <>
                    <Switch
                      id="uber-enabled-switch"
                      checked={field.value || false}
                      onChange={e =>
                        setFieldValue('uber.enabled', e.target.checked)
                      }
                    />
                    <InfoBox>
                      <strong className="font-bold">Note</strong>: Delivery fees from third-party integrations are overriden by system-set values.
                      Go over to <strong className="font-bold">Settings &gt; Services &gt; Deliveries &gt; Fees</strong> and set it to None.
                    </InfoBox>
                   </>
                  </FormGroup>
                  
                )}
              />
              <FastField
                name="uber.pickup_phone_number"
                render={({ field }: any) => (
                  <FormGroup
                    title="Pickup Phone Number"
                    help="Uber requires valid phone number from your store. This is autofilled up with your store phone number"
                  >
                    <>
                    <Input 
                      optional={true}
                      placeholder="Phone Number"
                      readOnly
                      type="text" {...field} value={this.injected.store.restaurant?.location.phone} 
                      />
                   
                   </>
                  </FormGroup>
                  
                )}
              />
              <FastField
                name="uber.abn"
                validate={(val: any) => this.validateRequiredField(val, form.values.uber?.enabled!)}
                render={({ field }: any) => (
                  <FormGroup className="no-border m-0">
                    <Input placeholder="ABN" type="text" {...field} value={field.value || ''} />
                    {this.getError(errors, 'abn') && (
                      <ErrorBox>{this.getError(errors, 'abn')}</ErrorBox>
                    )}
                  </FormGroup>
                )}
              />
              <FastField
                name="uber.company_name"
                validate={(val: any) => this.validateRequiredField(val, form.values.uber?.enabled!)}
                render={({ field }: any) => (
                  <FormGroup className="no-border m-0">
                    <Input placeholder="Company Name" type="text" {...field} value={field.value || ''} />
                    {this.getError(errors, 'company_name') && (
                      <ErrorBox>{this.getError(errors, 'company_name')}</ErrorBox>
                    )}
                  </FormGroup>
                )}
              />
              <FastField
                name="uber.trading_name"
                validate={(val: any) => this.validateRequiredField(val, form.values.uber?.enabled!)}
                render={({ field }: any) => (
                  <FormGroup className="no-border m-0">
                    <Input placeholder="Trading Name" type="text" {...field} value={field.value || ''} />
                    {this.getError(errors, 'trading_name') && (
                      <ErrorBox>{this.getError(errors, 'trading_name')}</ErrorBox>
                    )}
                  </FormGroup>
                )}
              />

              <FastField
                name="uber.director_name"
                validate={(val: any) => this.validateRequiredField(val, form.values.uber?.enabled!)}
                render={({ field }: any) => (
                  <FormGroup className="no-border m-0">
                    <Input placeholder="Director Name" type="text" {...field} value={field.value || ''} />
                    {this.getError(errors, 'director_name') && (
                      <ErrorBox>{this.getError(errors, 'director_name')}</ErrorBox>
                    )}
                  </FormGroup>
                )}
              />

              <FastField
                name="uber.mobile_number"
                validate={(val: any) => this.validateRequiredField(val, form.values.uber?.enabled!)}
                render={({ field }: any) => (
                  <FormGroup className="no-border m-0"> 
                    <Input placeholder="Mobile Number" type="text" {...field} value={field.value || ''} />
                    {this.getError(errors, 'mobile_number') && (
                      <ErrorBox>{this.getError(errors, 'mobile_number')}</ErrorBox>
                    )}
                  </FormGroup>
                )}
              />

              <FastField
                name="uber.agree"
                validate={(val: any) => this.validateRequiredCheckbox(val, form.values.uber?.enabled!)}
                render={({ field }: any) => (
                  <FormGroup className="no-border m-0">
                    <Checkbox
                      id="agree"
                      checked={field.value}
                      className="m-t-0 inline m-r-1"
                      onChange={(e) => {
                        setFieldValue("uber.agree", e.target.checked);
                      }}
                      label={
                        <p className="inline">
                            {'By enabling this feature, you understand and agree to the '}
                          <a className="link" onClick={(e) =>  {
                            e.preventDefault();
                            e.stopPropagation();
                            this.setState({termsModal: true});
                          }
                          }>Terms and Conditions.</a> 
                        </p>
                      }
                    />
                    
                    {this.getError(errors, 'agree') && (
                      <ErrorBox>{this.getError(errors, 'agree')}</ErrorBox>
                    )}
                  </FormGroup>
                )}
              />
              

              {error && <FormGroup error={error} />}

              <Button
                full={true}
                color="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && <RotateLoader size={2} color="white" />}
                {!isSubmitting && 'Save'}
              </Button>
            </>
          );
        }}
      </RestaurantForm>
      <Modal width={600} active={!!this.state.termsModal} close={() => this.setState({termsModal: false})}>
          <ModalTitle className="round-top">
            <h4>Terms and Conditions</h4>
          </ModalTitle>

          <ModalContent>
            <ol>
              <li>
                <strong className="font-bold">Definitions and Interpretation</strong>
                <TermsUL>
                  <li className="m-b-2">1.1 "Platform" - the online ordering system provided for hospitality businesses that integrate with Uber Direct.</li>
                  <li className="m-b-2">1.2 "Terms" - these terms and conditions that govern the use of the Platform.</li>
                  <li className="m-b-2">1.3 "User" or "You" - any person or entity that accesses or utilizes the Platform.</li>
                </TermsUL>
              </li>
              <li>
                <strong className="font-bold">Acceptance of Terms</strong>
                <p className="m-t-3 m-b-3">By accessing and using the Platform, the User acknowledges and agrees to be bound by these Terms. Users not accepting these Terms must not access or use the Uber Direct integration.</p>
              </li>
              <li>
                <strong className="font-bold">Third-Party Delivery Services</strong>
                <TermsUL>
                  <li className="m-b-2">3.1 The User acknowledges and agrees that all fees associated with the utilization of Uber Direct delivery services are the User's sole responsibility.</li>
                  <li className="m-b-2">3.2 The Company shall not be held liable for any issues, damages, or losses arising from the use of Uber Direct delivery services, including but not limited to matters involving drivers, food quality, or delivery times.</li>
                  <li className="m-b-2">3.3 The Company's role concerning Uber Direct delivery services is limited to facilitating the transmission of orders to Uber Direct and receiving the associated quotes and payments.</li>
                </TermsUL>
              </li>
              <li>
                <strong className="font-bold">Data Sharing Authorization</strong>
                <p className="m-t-3 m-b-3">The User represents and warrants that they possess the requisite authority to transmit data to Uber Direct under applicable laws and regulations.</p>
              </li>
              <li>
                <strong className="font-bold">Termination of Services</strong>
                <TermsUL>
                  <li className="m-b-2">5.1 The Company reserves the right to suspend, cancel, or terminate the provision of services under these Terms at its sole discretion and without prior notice.</li>
                  <li className="m-b-2">5.2 The User may terminate their use of the Delivery service integration by providing the Company with a written notice of termination no less than 30 days before the intended termination date.</li>
                  <li className="m-b-2">5.3 Before the termination of services, the User shall be responsible for settling all outstanding fees due to the Company.</li>
                </TermsUL>
              </li>
              <li>
                <strong className="font-bold">User Responsibilities</strong>
                <TermsUL>
                  <li className="m-b-2">6.1 The User shall be solely responsible for addressing and resolving disputes, refund requests, or other issues with their respective customers.</li>
                  <li className="m-b-2">6.2 The Company provides support services during regular business hours from Monday to Friday and disclaims any liability arising from using the third-party delivery service.</li>
                </TermsUL>
              </li>
              <li>
                <strong className="font-bold">Prohibited Activities</strong>
                <p className="m-t-3 m-b-3">The User is prohibited from using the Platform to facilitate the sale of illegal products, age-restricted items, or orders exceeding $500.</p>
              </li>
              <li>
                <strong className="font-bold">Fair Use of Support Services</strong>
                <p className="m-t-3 m-b-3">The Company reserves the right to impose additional charges for support services in cases where the User's utilization of such services is deemed excessive or unreasonable.</p>
              </li>
              <li>
                <strong className="font-bold">Intellectual Property Rights</strong>
                <p className="m-t-3 m-b-3">All intellectual property rights of the Platform are the Company's exclusive property. Intellectual property rights associated with Uber Direct remain the property of the respective third-party service provider.</p>
              </li>
              <li>
                <strong className="font-bold">Payment Terms</strong>
                <TermsUL>
                  <li className="m-b-2">10.1 The User shall be responsible for any additional charges not covered by the initial delivery quote, payable through the payment method on file or via invoice.</li>
                  <li className="m-b-2">10.2 Payment for invoices is due upon receipt.</li>
                  <li className="m-b-2">10.3 The Company reserves the right to modify pricing structures and service charges at its discretion.</li>
                </TermsUL>
              </li>
              <li>
                <strong className="font-bold">Compliance with Data Protection Laws</strong>
                <p className="m-t-3 m-b-3">The User shall comply with all applicable data protection laws and regulations in their jurisdiction and seek appropriate legal advice concerning the handling, storing, and processing of customer data.</p>
              </li>
              <li>
                <strong className="font-bold">Amendments to Terms</strong>
                <p className="m-t-3 m-b-3">The Company reserves the right to modify or amend these Terms at any time. The User is responsible for regularly reviewing these Terms for any updates or changes.</p>
              </li>
            </ol>
          </ModalContent>
        </Modal>
      </>
    );
  }
}
